/**
 * Le recouvrement : suivi, poste client, relances, encaissements.
 *
 * Maquettes validées les 09 et 10/09/2026, dans `docs/maquettes/` :
 * `recouvrement-encaissements.html`, `recouvrement-poste-client.html`,
 * `recouvrement-relances.html`.
 *
 * CET ÉCRAN NE CALCULE RIEN LUI-MÊME. Tout vient de `recouvrement.ts`, qui est
 * pur et testé seul : la balance âgée, le reste dû, le niveau de relance dû, le
 * compteur de ventilation. Ici il n'y a que du rendu et des appels.
 *
 * C'est ce qui permet de prouver qu'une balance tombe juste sans monter un
 * écran — et c'est déjà ce qui a trouvé un défaut le 10/09 : sans exception,
 * une facture jamais relancée recevait un « avis de compte », courrier d'AVANT
 * échéance, treize jours APRÈS son échéance.
 *
 *
 * L'ACCUEIL DU MODULE EST PROVISOIRE, ET C'EST DIT À L'ÉCRAN
 * ---------------------------------------------------------------------------
 * Décision de l'utilisateur du 09/09 : « chaque famille d'info doit pouvoir
 * rediriger vers une page correspondante, on va donc le traiter en dernier ».
 * Puis le 10/09 : « mets n'importe quel accueil pour le moment ».
 *
 * Celui-ci est donc quatre chiffres qui mènent chacun à leur page. Il ne
 * prétend à rien d'autre, et le dit.
 */

import { EcranRelancesPl } from './EcranRelancesPl.js';
import { EcranEncaissementsPl, EcranPostePl, EcranSuiviPl } from './EcranPostePl.js';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  corrigerUnEncaissement,
  gelerLesRelances,
  lireLePoste,
  marquerRelanceEnvoyee,
  saisirUnEncaissement,
  type CompteRenduClient,
  type EchecApi,
} from './api.js';
import { montant, saisieBrute, dateDite } from './facturation.js';
import {
  affecterAuPlusAncien,
  affectationDite,
  canalRelanceDit,
  CANAUX_RELANCE,
  centimes,
  compteur,
  decompteEncaissements,
  decomptePoste,
  decompteRelances,
  MODES,
  modeDit,
  MOTIFS_GEL,
  motifDit,
  niveauDit,
  resteDu,
  retardMoyenDit,
  TRANCHES,
  totauxBalance,
  type Acompte,
  type CourrierARelancer,
  type Encaissement,
  type LigneBalance,
  type PieceRecouvrement,
} from './recouvrement.js';

interface Proprietes {
  /**
   * LE JETON EST UNE FONCTION, pas une chaîne : il se rafraîchit, et un jeton
   * capturé au montage serait périmé au bout d'une heure d'écran ouvert. Même
   * signature que `EcranComptabilite`, qui porte cet écran.
   */
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  /** LOT 158 : incrémentée quand le bandeau Pennylane a relu la copie. */
  readonly version?: number;
}

/** Le jour, en UTC — le même repère que tous les calculs du module. */
function aujourdhuiIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Une ligne d'affectation en cours de saisie. */
interface LigneSaisie {
  readonly facture_id: string;
  readonly montant: string;
}

// =============================================================================
// L'accueil du module — PROVISOIRE
// =============================================================================

export function VueSuivi(p: {
  readonly lignes: readonly LigneBalance[];
  readonly courriers: readonly CourrierARelancer[];
  readonly quandAller: (onglet: string) => void;
}) {
  const totaux = totauxBalance(p.lignes);
  const enRetard = p.courriers.filter((c) => c.etat === 'retard');
  const duJour = p.courriers.filter((c) => c.etat === 'jour');
  const geles = p.lignes.flatMap((l) => l.gels);

  const vignettes: readonly {
    readonly cle: string;
    readonly titre: string;
    readonly valeur: string;
    readonly note: string;
    readonly vers: string;
    readonly alerte: boolean;
  }[] = [
    {
      cle: 'echu',
      titre: 'Échu',
      valeur: `${montant(totaux.echu)} €`,
      note: `sur ${montant(totaux.total)} € d’encours`,
      vers: 'poste',
      alerte: totaux.echu > 0,
    },
    {
      cle: 'retard',
      titre: 'Relances en retard d’envoi',
      valeur: String(enRetard.length),
      note:
        enRetard.length === 0
          ? 'rien n’a été sauté'
          : `${montant(enRetard.reduce((s, c) => s + c.montant, 0))} € concernés`,
      vers: 'relances',
      alerte: enRetard.length > 0,
    },
    {
      cle: 'jour',
      titre: 'À envoyer aujourd’hui',
      valeur: String(duJour.length),
      note: duJour.length === 0 ? 'rien de nouveau ce jour' : 'prêtes à partir',
      vers: 'relances',
      alerte: false,
    },
    {
      cle: 'acompte',
      titre: 'Acomptes non affectés',
      valeur: `${montant(-totaux.acompte)} €`,
      note:
        geles.length === 0
          ? 'aucune pièce gelée'
          : `${String(geles.length)} pièce${geles.length > 1 ? 's gelées' : ' gelée'}`,
      vers: 'encaissements',
      alerte: false,
    },
  ];

  return (
    <>
      {/* ON DIT QUE C'EST PROVISOIRE plutôt que de le laisser croire fini. */}
      {/* TEXTE EXPLICATIF RETIRÉ — refonte graphique, maquettes validées le 31/10/2026 : « un logiciel ne se commente pas lui-même ». */}
      {/* SON PROPRE HABILLAGE, et non celui de l'écran d'accueil : les classes
          `vignette-accueil` ne vivent que dans `.ecran-accueil`, qui pose la
          grille et les fonds. Posées ici, elles rendaient une bande brune —
          constaté à la capture le 10/09/2026, pas deviné. */}
      <div className="tuiles-recouvrement">
        {vignettes.map((v) => (
          <button
            key={v.cle}
            type="button"
            className={v.alerte ? 'tuile-recouvrement alerte' : 'tuile-recouvrement'}
            onClick={() => {
              p.quandAller(v.vers);
            }}
          >
            <span className="titre-tuile">{v.titre}</span>
            <span className="valeur-tuile">{v.valeur}</span>
            <span className="note-tuile">{v.note}</span>
          </button>
        ))}
      </div>
    </>
  );
}

// =============================================================================
// Le poste client
// =============================================================================

export function VuePoste(p: {
  readonly lignes: readonly LigneBalance[];
  readonly aujourdhui: string;
  readonly occupe: boolean;
  /** Le gel porte sur LA PIÈCE — corrigé le 10/09/2026. */
  readonly quandGeler: (piece: PieceRecouvrement) => void;
  readonly quandLever: (piece: PieceRecouvrement) => void;
  readonly quandEncaisser: (ligne: LigneBalance) => void;
  /**
   * OUVRIR LE COMPTE RENDU — 14/09/2026, maquette vue 1, validée.
   *
   * LE DÉPLIAGE A DISPARU AVEC CETTE PROPRIÉTÉ. Il insérait un TABLEAU DANS LE
   * TABLEAU, avec ses propres en-têtes, sous une rangée de neuf colonnes :
   * deux clients ouverts et l'œil ne savait plus quel en-tête gouvernait quelle
   * colonne. Demande de Mohamed, mot pour mot : « on ne peut pas déballer les
   * factures mais en appuyant sur la ligne on a le compte rendu ».
   *
   * FACULTATIVE : `apercu.tsx` monte cette vue hors de l'application et n'a
   * nulle part où ouvrir. Sans elle, la ligne n'est simplement pas cliquable.
   */
  readonly quandOuvrir?: ((ligne: LigneBalance) => void) | undefined;
}) {
  const totaux = totauxBalance(p.lignes);

  if (p.lignes.length === 0) {
    return (
      <div className="vide">
        <p>Aucun client ne doit quoi que ce soit. Tout est soldé.</p>
      </div>
    );
  }

  return (
    <>
      <p className="decompte">{decomptePoste(p.lignes)}</p>
      <div className="tableau-cadre">
        <table className="table-balance">
          <thead>
            <tr>
              <th scope="col">Client</th>
              <th scope="col" className="n">
                À échoir
              </th>
              {TRANCHES.map((t) => (
                <th key={t.cle} scope="col" className="n">
                  {t.nom}
                </th>
              ))}
              <th scope="col" className="n">
                Acompte
              </th>
              <th scope="col" className="n">
                Total dû
              </th>
              {/* CE QUE L'UTILISATEUR APPELAIT « garder en mémoire le temps de
                  retard avant paiement ». Rien n'est gardé : c'est la moyenne
                  des délais de règlement de ses pièces SOLDÉES, recalculée à
                  chaque lecture. */}
              <th scope="col" className="n">
                Retard moyen
              </th>
            </tr>
          </thead>
          <tbody>
            {p.lignes.map((ligne) => {
              const cellule = (valeur: number): string =>
                valeur === 0 ? '—' : montant(valeur);
              const tout =
                ligne.gels.length > 0 && ligne.gels.length === ligne.pieces.length;
              const classes = ['ligne-balance'];
              // GRISÉE SEULEMENT SI TOUT EST GELÉ. Un client dont une pièce sur
              // deux est gelée continue d'être relancé sur l'autre : le pâlir
              // en entier dirait le contraire.
              if (tout) classes.push('eteinte');
              if (p.quandOuvrir !== undefined) classes.push('ligne-ouvrable');
              return (
                <tr
                  key={ligne.clientId}
                  className={classes.join(' ')}
                  /**
                   * TOUTE LA RANGÉE OUVRE, et non un bouton dans la première
                   * cellule : il n'y a plus rien d'autre à cliquer ici, donc
                   * aucune ambiguïté possible. C'est la différence avec « À
                   * facturer », où une case à cocher partage la rangée et où
                   * seul le numéro est cliquable.
                   *
                   * `role` ET `tabIndex` : une rangée n'est pas un bouton pour
                   * le navigateur. Sans eux, le compte rendu serait
                   * inatteignable au clavier — et « Entrée » ne ferait rien.
                   */
                  {...(p.quandOuvrir === undefined
                    ? {}
                    : {
                        role: 'button',
                        tabIndex: 0,
                        title: `Ouvrir le compte rendu de ${ligne.clientNom}`,
                        onClick: () => p.quandOuvrir?.(ligne),
                        onKeyDown: (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            p.quandOuvrir?.(ligne);
                          }
                        },
                      })}
                >
                  <td>
                    {ligne.clientNom}
                    {/* CE QUI FAISAIT DÉPLIER MONTE DANS LA LISTE : le nombre
                        de pièces, et ce qui cloche. L'information est là sans
                        qu'on ait à ouvrir. */}
                    <span className="secondaire">
                      {String(ligne.pieces.length)} pièce
                      {ligne.pieces.length > 1 ? 's' : ''}
                      {ligne.gels.length > 0 &&
                        ` · ${String(ligne.gels.length)} gelée${ligne.gels.length > 1 ? 's' : ''}`}
                    </span>
                    {/* LA BARRE D'ÂGE DU TÉLÉPHONE — lot 133, maquette du
                        01/11/2026. Les sept colonnes de montants ne tiennent
                        pas sur 390 px : la même répartition se lit en une
                        barre, de « à échoir » (gris) à « + de 60 j » (rouge).
                        Cachée sur ordinateur, où les colonnes la disent. Les
                        acomptes, négatifs, n'y entrent pas. */}
                    {(() => {
                      const parts = [
                        { cle: 'a-echoir', valeur: ligne.colonnes['a-echoir'] ?? 0 },
                        ...TRANCHES.map((tr) => ({ cle: tr.cle, valeur: ligne.colonnes[tr.cle] ?? 0 })),
                      ].filter((x) => x.valeur > 0);
                      const somme = parts.reduce((s, x) => s + x.valeur, 0);
                      if (somme <= 0) return null;
                      return (
                        <span className="barre-age-tel" aria-hidden="true">
                          {parts.map((x) => (
                            <span
                              key={x.cle}
                              className={`age-${x.cle}`}
                              style={{ width: `${String((x.valeur / somme) * 100)}%` }}
                            />
                          ))}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="n code">{cellule(ligne.colonnes['a-echoir'] ?? 0)}</td>
                  {TRANCHES.map((t) => (
                    <td key={t.cle} className="n code">
                      {cellule(ligne.colonnes[t.cle] ?? 0)}
                    </td>
                  ))}
                  <td className="n code">{cellule(ligne.acompte)}</td>
                  <td className="n code total-balance">{montant(ligne.total)}</td>
                  <td className="n maigre">{retardMoyenDit(ligne.retardMoyen)}</td>
                </tr>
              );
            })}
            {/* LA LIGNE QUI FAIT TOMBER LE TABLEAU JUSTE À LA VERTICALE. */}
            <tr className="pied-balance">
              <td>Total</td>
              <td className="n code">{montant(totaux.colonnes['a-echoir'] ?? 0)}</td>
              {TRANCHES.map((t) => (
                <td key={t.cle} className="n code">
                  {montant(totaux.colonnes[t.cle] ?? 0)}
                </td>
              ))}
              <td className="n code">{montant(totaux.acompte)}</td>
              <td className="n code total-balance">{montant(totaux.total)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}


// =============================================================================
// Le compte rendu d'un client
// =============================================================================

/** « relance1 » → « Relance 1 ». Le texte se compose ICI, jamais en base. */
function genreDit(genre: string, precision: string | null): string {
  if (genre === 'emission') {
    return precision === 'avoir' ? 'Avoir émis' : 'Facture émise';
  }
  if (genre === 'reglement') {
    return `Règlement reçu${precision === null ? '' : ` par ${precision}`}`;
  }
  if (genre === 'gel') {
    return `Gel posé${precision === null ? '' : ` — ${motifDit(precision).toLowerCase()}`}`;
  }
  if (genre === 'relance') {
    return precision === null ? 'Relance envoyée' : `${niveauDit(precision)} envoyée`;
  }
  return genre;
}

/**
 * LE COMPTE RENDU D'UN CLIENT — 14/09/2026, maquette vue 1, validée.
 *
 * Ce qu'on veut quand le client appelle : combien il doit, depuis quand, ce
 * qu'il a payé, qui appeler, et ce qui s'est passé.
 *
 * DEUX SOURCES, ET C'EST VOULU :
 *   - `ligne` vient de la LISTE, déjà calculée par `balanceAgee()`. Les totaux
 *     et les pièces affichés ici sont donc, au chiffre près, ceux de la rangée
 *     qu'on vient de cliquer. Les redemander à l'API en ferait deux calculs,
 *     puis un jour deux chiffres ;
 *   - `rendu` vient de `GET /recouvrement/poste/:id` et ne porte QUE ce que la
 *     liste n'a pas : les contacts, les conditions, les règlements, le fil.
 */
export function VueCompteClient(p: {
  readonly ligne: LigneBalance;
  readonly rendu: CompteRenduClient | null;
  readonly occupe: boolean;
  readonly quandRetour: () => void;
  readonly quandGeler: (piece: PieceRecouvrement) => void;
  readonly quandLever: (piece: PieceRecouvrement) => void;
  readonly quandEncaisser: (ligne: LigneBalance) => void;
}) {
  const echu = TRANCHES.reduce(
    (somme, t) => somme + (p.ligne.colonnes[t.cle] ?? 0),
    0,
  );
  const aEchoir = p.ligne.colonnes['a-echoir'] ?? 0;

  return (
    <>
      <div className="bandeau-fiche-client">
        <button className="retour" type="button" onClick={p.quandRetour}>
          ← Poste client
        </button>
        <b>{p.ligne.clientNom}</b>
        {p.rendu?.client.code != null && (
          <span className="statut douce">{p.rendu.client.code}</span>
        )}
        <span className="a-droite">
          <button
            type="button"
            className="bouton"
            disabled={p.occupe}
            onClick={() => {
              p.quandEncaisser(p.ligne);
            }}
          >
            Saisir un encaissement
          </button>
        </span>
      </div>

      {/* LES CHIFFRES QUI RÉPONDENT AU TÉLÉPHONE. Ils viennent de la rangée
          cliquée, pas d'un second calcul. */}
      <div className="bandes-client">
        <div className="bande">
          <span className="etiq">Total dû</span>
          <b className="code gros">{montant(p.ligne.total)}</b>
        </div>
        <div className="bande">
          <span className="etiq">Échu</span>
          <b className={echu === 0 ? 'code gros' : 'code gros tiede'}>
            {montant(echu)}
          </b>
        </div>
        <div className="bande">
          <span className="etiq">À échoir</span>
          <b className="code gros">{montant(aEchoir)}</b>
        </div>
        <div className="bande">
          <span className="etiq">Retard moyen de règlement</span>
          <b className="code gros">{retardMoyenDit(p.ligne.retardMoyen)}</b>
        </div>
        <div className="bande">
          <span className="etiq">Conditions de paiement</span>
          <b className="gros">
            {p.rendu?.client.conditions_paiement ?? '—'}
          </b>
        </div>
      </div>

      <div className="deux-colonnes-client">
        <div>
          <h3 className="titre-bloc">Les pièces ouvertes</h3>
          <div className="tableau-cadre">
            <table>
              <thead>
                <tr>
                  <th scope="col">Pièce</th>
                  <th scope="col">Échéance</th>
                  <th scope="col" className="n">
                    Porté au compte
                  </th>
                  <th scope="col" className="n">
                    Réglé
                  </th>
                  <th scope="col" className="n">
                    Reste dû
                  </th>
                  <th scope="col">Relance</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {p.ligne.pieces.map((piece) => {
                  const gel =
                    p.ligne.gels.find((g) => g.facture_id === piece.id) ?? null;
                  return (
                    <tr key={piece.id} className={gel === null ? undefined : 'eteinte'}>
                      <td className="code">
                        {piece.numero ?? '—'}
                        <span className="secondaire">
                          {piece.type === 'avoir' ? 'Avoir' : 'Facture'}
                        </span>
                      </td>
                      <td className="code">
                        {piece.echeance === null ? '—' : dateDite(piece.echeance)}
                      </td>
                      <td className="n code">
                        {montant(resteDu(piece) + centimes(piece.regle))}
                      </td>
                      <td className="n code">
                        {centimes(piece.regle) === 0
                          ? '—'
                          : montant(centimes(piece.regle))}
                      </td>
                      <td className="n code">{montant(resteDu(piece))}</td>
                      <td>
                        {piece.relance_niveau === null ? (
                          <span className="statut pas-envoye">Aucune</span>
                        ) : (
                          <>
                            <span className="statut brouillon">
                              {niveauDit(piece.relance_niveau)}
                            </span>
                            <span className="secondaire">
                              envoyée le {dateDite(piece.relance_le)}
                            </span>
                          </>
                        )}
                        {gel !== null && (
                          <span className="secondaire">
                            Gelée — {motifDit(gel.motif).toLowerCase()}, reprise le{' '}
                            {dateDite(gel.reprise_le)}
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="bouton bouton-discret"
                          disabled={p.occupe}
                          onClick={() => {
                            if (gel === null) p.quandGeler(piece);
                            else p.quandLever(piece);
                          }}
                        >
                          {gel === null ? 'Geler' : 'Lever le gel'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h3 className="titre-bloc">Les derniers règlements</h3>
          {p.rendu === null ? (
            <p className="attente">Lecture du compte…</p>
          ) : p.rendu.reglements.length === 0 ? (
            <p className="maigre encart-client">
              Aucun règlement enregistré pour ce client.
            </p>
          ) : (
            <div className="tableau-cadre">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Reçu le</th>
                    <th scope="col">Moyen</th>
                    <th scope="col">Référence</th>
                    <th scope="col" className="n">
                      Montant
                    </th>
                    <th scope="col">Affecté à</th>
                  </tr>
                </thead>
                <tbody>
                  {p.rendu.reglements.map((r) => (
                    <tr key={r.id}>
                      <td className="code">{dateDite(r.recu_le)}</td>
                      <td>{modeDit(r.mode)}</td>
                      <td className="code">{r.reference ?? '—'}</td>
                      <td className="n code">{montant(centimes(r.montant))}</td>
                      {/* CE QUI RESTE NON LETTRÉ SE DIT ICI : c'est l'acompte
                          que la liste montre en négatif, et la question qu'on
                          pose au téléphone. */}
                      <td className="code">
                        {r.pieces ?? '—'}
                        {centimes(r.affecte) < centimes(r.montant) && (
                          <span className="secondaire">
                            {montant(centimes(r.montant) - centimes(r.affecte))} non
                            affecté
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          <h3 className="titre-bloc">Qui appeler</h3>
          <div className="encart-client">
            {p.rendu === null ? (
              <p className="attente">Lecture du compte…</p>
            ) : (
              <>
                {p.rendu.client.courriel_facturation != null && (
                  <p>
                    <b>Les factures partent à</b>
                    <br />
                    <span className="maigre">{p.rendu.client.courriel_facturation}</span>
                  </p>
                )}
                {p.rendu.contacts.map((c) => (
                  <p key={c.id}>
                    <b>{c.nom}</b>
                    {c.fonction != null && (
                      <span className="maigre"> — {c.fonction}</span>
                    )}
                    <br />
                    <span className="maigre">
                      {[c.courriel, c.telephone].filter((x) => x != null).join(' · ') ||
                        'Aucune coordonnée'}
                    </span>
                  </p>
                ))}
                {p.rendu.contacts.length === 0 &&
                  p.rendu.client.courriel_facturation == null && (
                    <p className="maigre">
                      Aucun contact enregistré. Ils se saisissent dans l’Annuaire,
                      sur la fiche du client.
                    </p>
                  )}
              </>
            )}
          </div>

          <h3 className="titre-bloc">Ce qui s’est passé</h3>
          <div className="encart-client">
            {p.rendu === null ? (
              <p className="attente">Lecture du compte…</p>
            ) : p.rendu.historique.length === 0 ? (
              <p className="maigre">Rien d’enregistré sur ce compte.</p>
            ) : (
              <ul className="fil-client">
                {p.rendu.historique.map((h, rang) => (
                  <li key={`${h.genre}-${h.jour}-${String(rang)}`}>
                    <span className="code maigre">{dateDite(h.jour)}</span>
                    {genreDit(h.genre, h.precision)}
                    {h.piece != null && <span className="code"> {h.piece}</span>}
                    {h.genre === 'reglement' && h.valeur != null && (
                      <span className="code"> — {montant(centimes(h.valeur))}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {/* CE QUE LE FIL NE MONTRE PAS, ET IL FAUT LE DIRE : les
                corrections de saisie ne sont notées nulle part dans Stafa. Un
                fil qui se tairait là-dessus se ferait passer pour un registre
                comptable. */}
            {/* TEXTE EXPLICATIF RETIRÉ — refonte graphique, maquettes validées le 31/10/2026 : « un logiciel ne se commente pas lui-même ». */}
          </div>
        </div>
      </div>
    </>
  );
}

// =============================================================================
// Les relances
// =============================================================================

export function VueRelances(p: {
  readonly courriers: readonly CourrierARelancer[];
  readonly occupe: boolean;
  readonly quandEnvoyer: (courrier: CourrierARelancer) => void;
}) {
  if (p.courriers.length === 0) {
    return (
      <div className="vide">
        <p>
          Rien à relancer. Aucune pièce échue n’attend un courrier — ni en retard, ni ce
          jour.
        </p>
      </div>
    );
  }

  const familles: readonly {
    readonly cle: CourrierARelancer['etat'];
    readonly titre: string;
  }[] = [
    { cle: 'retard', titre: 'En retard d’envoi' },
    { cle: 'jour', titre: 'À envoyer aujourd’hui' },
    { cle: 'gele', titre: 'Gelées' },
  ];

  return (
    <>
      <p className="decompte">{decompteRelances(p.courriers)}</p>
      <div className="tableau-cadre">
        <table className="table-relances">
          <thead>
            <tr>
              <th scope="col">Client</th>
              <th scope="col">Pièce(s)</th>
              <th scope="col">Niveau à envoyer</th>
              <th scope="col">Dernier envoi</th>
              <th scope="col">Canal</th>
              <th scope="col" className="n">
                Reste dû
              </th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {familles.map((famille) => {
              const groupe = p.courriers.filter((c) => c.etat === famille.cle);
              if (groupe.length === 0) return null;
              const somme = groupe.reduce((s, c) => s + c.montant, 0);
              return (
                <Fragment key={famille.cle}>
                  <tr className="ligne-client">
                    <td colSpan={7}>
                      {famille.titre} — {String(groupe.length)} courrier
                      {groupe.length > 1 ? 's' : ''}, {montant(somme)} €
                    </td>
                  </tr>
                  {groupe.map((courrier) => (
                    <tr
                      key={courrier.clef}
                      className={courrier.etat === 'gele' ? 'eteinte' : undefined}
                    >
                      <td>
                        {courrier.clientNom}
                        {courrier.gel !== null && (
                          <span className="secondaire">
                            {motifDit(courrier.gel.motif)} — reprise le{' '}
                            {dateDite(courrier.gel.reprise_le)}
                          </span>
                        )}
                      </td>
                      <td className="code">
                        {courrier.pieces.map((piece) => (
                          <div key={piece.id}>{piece.numero ?? '—'}</div>
                        ))}
                      </td>
                      <td>
                        <span
                          className={
                            courrier.etat === 'retard'
                              ? 'statut retard'
                              : courrier.etat === 'jour'
                                ? 'statut a-venir'
                                : 'statut brouillon'
                          }
                        >
                          {courrier.niveauNom}
                        </span>
                        <span className="secondaire">
                          {courrier.etat === 'gele'
                            ? `gelée — due depuis le ${dateDite(courrier.dueLe) ?? ''}`
                            : courrier.retard === 0
                              ? 'échue ce jour'
                              : `due depuis le ${dateDite(courrier.dueLe) ?? ''} — ${String(courrier.retard)} j`}
                        </span>
                      </td>
                      <td className="maigre">
                        {courrier.pieces[0]?.relance_niveau === null ||
                        courrier.pieces[0] === undefined ? (
                          <span className="statut pas-envoye">— aucun</span>
                        ) : (
                          <>
                            {niveauDit(courrier.pieces[0].relance_niveau)}
                            <span className="secondaire">
                              le {dateDite(courrier.pieces[0].relance_le)}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="maigre">{canalRelanceDit(courrier.canal)}</td>
                      <td className="n code">{montant(courrier.montant)}</td>
                      <td>
                        <button
                          type="button"
                          className="bouton bouton-discret"
                          disabled={p.occupe || courrier.etat === 'gele'}
                          onClick={() => {
                            p.quandEnvoyer(courrier);
                          }}
                        >
                          Marquer envoyée
                        </button>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// =============================================================================
// Le journal des encaissements
// =============================================================================

export function VueEncaissements(p: {
  readonly encaissements: readonly Encaissement[];
  readonly occupe: boolean;
  readonly quandOuvrir: (encaissement: Encaissement) => void;
  readonly quandSupprimer: (encaissement: Encaissement) => void;
}) {
  if (p.encaissements.length === 0) {
    return (
      <div className="vide">
        <p>
          Aucun encaissement sur la période. Élargissez les dates ou saisissez-en un.
        </p>
      </div>
    );
  }
  return (
    <>
      <p className="decompte">{decompteEncaissements(p.encaissements)}</p>
      <div className="tableau-cadre">
        <table>
          <thead>
            <tr>
              <th scope="col">Reçu le</th>
              <th scope="col">Client</th>
              <th scope="col">Mode</th>
              <th scope="col">Affecté à</th>
              <th scope="col">Saisi par</th>
              <th scope="col" className="n">
                Montant
              </th>
              <th scope="col">Affectation</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {p.encaissements.map((encaissement) => {
              const dit = affectationDite(encaissement);
              return (
                <tr
                  key={encaissement.id}
                  className="rangee"
                  onClick={() => {
                    p.quandOuvrir(encaissement);
                  }}
                >
                  <td className="code">{dateDite(encaissement.recu_le)}</td>
                  <td>{encaissement.client_nom}</td>
                  <td>
                    {modeDit(encaissement.mode)}
                    {encaissement.reference !== null && (
                      <span className="secondaire">{encaissement.reference}</span>
                    )}
                  </td>
                  <td className="code">
                    {encaissement.affectations.length === 0
                      ? '—'
                      : encaissement.affectations
                          .map((a) => a.numero ?? '—')
                          .join(', ')}
                  </td>
                  <td>{encaissement.saisi_par}</td>
                  <td className="n code">{montant(centimes(encaissement.montant))}</td>
                  <td>
                    <span
                      className={
                        dit.etat === 'solde'
                          ? 'statut envoye'
                          : dit.etat === 'partiel'
                            ? 'statut attente'
                            : 'statut retard'
                      }
                    >
                      {dit.mot}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="bouton bouton-risque"
                      disabled={p.occupe}
                      onClick={(e) => {
                        e.stopPropagation();
                        p.quandSupprimer(encaissement);
                      }}
                    >
                      Effacer
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// =============================================================================
// La fenêtre de saisie d'un encaissement
// =============================================================================

interface Brouillon {
  readonly id: string | null;
  readonly clientId: string;
  readonly recuLe: string;
  readonly mode: string;
  readonly reference: string;
  readonly montant: string;
  readonly lignes: readonly LigneSaisie[];
}

function FenetreEncaissement(p: {
  readonly brouillon: Brouillon;
  readonly clients: readonly { readonly id: string; readonly nom: string }[];
  readonly pieces: readonly PieceRecouvrement[];
  readonly occupe: boolean;
  readonly quandChanger: (brouillon: Brouillon) => void;
  readonly quandEnregistrer: () => void;
  readonly quandAnnuler: () => void;
}) {
  const ouvertes = useMemo(
    () =>
      p.pieces
        .filter(
          (piece) => piece.client_id === p.brouillon.clientId && resteDu(piece) !== 0,
        )
        .slice()
        .sort((a, b) =>
          (a.echeance ?? a.date_piece).localeCompare(b.echeance ?? b.date_piece),
        ),
    [p.pieces, p.brouillon.clientId],
  );

  const etat = compteur(saisieBrute(p.brouillon.montant), p.brouillon.lignes);
  const montantDe = (factureId: string): string =>
    p.brouillon.lignes.find((l) => l.facture_id === factureId)?.montant ?? '';

  const poser = (factureId: string, valeur: string): void => {
    const restantes = p.brouillon.lignes.filter((l) => l.facture_id !== factureId);
    const nettoye = saisieBrute(valeur);
    p.quandChanger({
      ...p.brouillon,
      lignes:
        nettoye === '' || Number(nettoye) === 0
          ? restantes
          : [...restantes, { facture_id: factureId, montant: nettoye }],
    });
  };

  return (
    <div className="voile-facture" role="presentation">
      <div
        className="fenetre-facture"
        role="dialog"
        aria-modal="true"
        aria-label="Encaissement"
      >
        <div className="entete-facture">
          <h2>{p.brouillon.id === null ? 'Nouvel encaissement' : 'Encaissement'}</h2>
        </div>

        <div className="grille-saisie">
          <label>
            Client
            <select
              className="champ"
              value={p.brouillon.clientId}
              disabled={p.occupe || p.brouillon.id !== null}
              onChange={(e) => {
                // CHANGER DE CLIENT VIDE LA VENTILATION : les pièces ne sont
                // plus les siennes, et l'API refuserait de toute façon.
                p.quandChanger({
                  ...p.brouillon,
                  clientId: e.target.value,
                  lignes: [],
                });
              }}
            >
              <option value="">Choisir…</option>
              {p.clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.nom}
                </option>
              ))}
            </select>
          </label>
          <label>
            Reçu le
            <input
              className="champ"
              type="date"
              value={p.brouillon.recuLe}
              disabled={p.occupe}
              onChange={(e) => {
                p.quandChanger({ ...p.brouillon, recuLe: e.target.value });
              }}
            />
          </label>
          <label>
            Mode
            <select
              className="champ"
              value={p.brouillon.mode}
              disabled={p.occupe}
              onChange={(e) => {
                p.quandChanger({ ...p.brouillon, mode: e.target.value });
              }}
            >
              {MODES.map((mode) => (
                <option key={mode.cle} value={mode.cle}>
                  {mode.nom}
                </option>
              ))}
            </select>
          </label>
          <label>
            Référence
            <input
              className="champ"
              value={p.brouillon.reference}
              disabled={p.occupe}
              placeholder="facultative"
              onChange={(e) => {
                p.quandChanger({ ...p.brouillon, reference: e.target.value });
              }}
            />
          </label>
          <label>
            Montant reçu
            <input
              className="champ"
              inputMode="decimal"
              value={p.brouillon.montant}
              disabled={p.occupe}
              onChange={(e) => {
                p.quandChanger({ ...p.brouillon, montant: e.target.value });
              }}
            />
          </label>
        </div>

        <div className="actions">
          <span className="titre-groupe">Ce que ce règlement solde</span>
          <button
            type="button"
            className="bouton"
            disabled={p.occupe || p.brouillon.clientId === ''}
            onClick={() => {
              p.quandChanger({
                ...p.brouillon,
                lignes: affecterAuPlusAncien(
                  saisieBrute(p.brouillon.montant),
                  ouvertes,
                ).map((a) => ({
                  facture_id: a.facture_id,
                  montant: (a.montant / 100).toFixed(2),
                })),
              });
            }}
          >
            Affecter au plus ancien
          </button>
          <button
            type="button"
            className="bouton bouton-discret"
            disabled={p.occupe || p.brouillon.lignes.length === 0}
            onClick={() => {
              p.quandChanger({ ...p.brouillon, lignes: [] });
            }}
          >
            Tout remettre à zéro
          </button>
        </div>

        {ouvertes.length === 0 ? (
          <div className="vide">
            <p>
              Ce client n’a aucune pièce ouverte. L’encaissement peut s’enregistrer
              quand même — il restera en acompte sur son compte.
            </p>
          </div>
        ) : (
          <div className="tableau-cadre">
            <table>
              <thead>
                <tr>
                  <th scope="col">Pièce</th>
                  <th scope="col">Échéance</th>
                  <th scope="col" className="n">
                    Reste dû
                  </th>
                  <th scope="col" className="n">
                    On affecte
                  </th>
                </tr>
              </thead>
              <tbody>
                {ouvertes.map((piece) => (
                  <tr key={piece.id}>
                    <td className="code">
                      {piece.numero ?? '—'}
                      <span className="secondaire">
                        {piece.type === 'avoir' ? 'Avoir' : 'Facture'}
                      </span>
                    </td>
                    <td className="code">
                      {piece.echeance === null ? '—' : dateDite(piece.echeance)}
                    </td>
                    <td className="n code">{montant(resteDu(piece))}</td>
                    <td className="n saisie-ligne">
                      <input
                        inputMode="decimal"
                        value={montantDe(piece.id)}
                        disabled={p.occupe}
                        aria-label={`Montant affecté à ${piece.numero ?? 'cette pièce'}`}
                        onChange={(e) => {
                          poser(piece.id, e.target.value);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="pied-facture">
          <button
            type="button"
            className="bouton"
            disabled={p.occupe || !etat.enregistrable || p.brouillon.clientId === ''}
            onClick={p.quandEnregistrer}
          >
            Enregistrer
          </button>
          <button
            type="button"
            className="bouton bouton-discret"
            disabled={p.occupe}
            onClick={p.quandAnnuler}
          >
            Annuler
          </button>
          {/* LE COMPTEUR COMMANDE LE BOUTON. Vert à zéro, orange s'il reste —
              le reste part en acompte —, rouge si l'on dépasse, et là on
              n'enregistre pas : c'est une faute de frappe, pas une intention. */}
          <span className="compteur">
            <span className="maigre">
              Reçu {montant(etat.recu)} · affecté {montant(etat.affecte)}
            </span>{' '}
            Reste à affecter{' '}
            <span
              className={
                etat.etat === 'juste'
                  ? 'code fait'
                  : etat.etat === 'acompte'
                    ? 'code attente'
                    : 'code danger'
              }
            >
              {montant(etat.reste)}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// L'écran
// =============================================================================

export function EcranRecouvrement({ jeton, quandAccesPerdu, version = 0 }: Proprietes) {
  const aujourdhui = aujourdhuiIso();

  const [onglet, setOnglet] = useState('suivi');
  /** LOT 159 : le client cliqué dans le Suivi, ouvert d'office dans le poste. */
  const [clientDuSuivi, setClientDuSuivi] = useState<string | null>(null);
  const [panne, setPanne] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const [pieces, setPieces] = useState<readonly PieceRecouvrement[]>([]);
  const [acomptes, setAcomptes] = useState<readonly Acompte[]>([]);

  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  const [gelDemande, setGelDemande] = useState<PieceRecouvrement | null>(null);
  const [envoiDemande, setEnvoiDemande] = useState<CourrierARelancer | null>(null);
  const [envoiLe, setEnvoiLe] = useState('');
  const [envoiCanal, setEnvoiCanal] = useState('courriel');
  const [gelMotif, setGelMotif] = useState('promesse');
  const [gelReprise, setGelReprise] = useState('');

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  const dire = useCallback(
    (cause: EchecApi): void => {
      if (cause === 'acces') quandAccesPerdu();
      setRefus(REFUS[cause]);
    },
    [quandAccesPerdu],
  );

  const relire = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;
    const poste = await lireLePoste(j);
    if (poste.etat !== 'ok') {
      if (poste.cause === 'acces') quandAccesPerdu();
      setPanne(true);
      return;
    }
    setPanne(false);
    setPieces(poste.valeur.pieces);
    setAcomptes(poste.valeur.acomptes);
  }, [avecJeton, quandAccesPerdu]);

  useEffect(() => {
    void relire();
  }, [relire]);

  /**
   * LES CLIENTS PROPOSÉS À LA SAISIE sont ceux qui ont un mouvement — une pièce
   * émise ou un acompte. C'est une limite assumée : un client qui paierait
   * avant toute facture n'y serait pas. Le jour où ça arrive, il faudra lire
   * l'annuaire ici ; aujourd'hui ce serait une requête de plus pour un cas qui
   * ne s'est jamais produit.
   */
  const clients = useMemo(() => {
    const vus = new Map<string, string>();
    for (const piece of pieces) vus.set(piece.client_id, piece.client_nom);
    for (const acompte of acomptes) vus.set(acompte.client_id, acompte.client_nom);
    return [...vus.entries()]
      .map(([id, nom]) => ({ id, nom }))
      .sort((a, b) => a.nom.localeCompare(b.nom));
  }, [pieces, acomptes]);

  const agir = useCallback(
    async (
      travail: (
        j: string,
      ) => Promise<{ etat: 'ok' } | { etat: 'echec'; cause: EchecApi }>,
    ) => {
      const j = await avecJeton();
      if (j === null) return false;
      setOccupe(true);
      setRefus(null);
      const faite = await travail(j);
      if (faite.etat !== 'ok') {
        setOccupe(false);
        dire(faite.cause);
        return false;
      }
      await relire();
      setOccupe(false);
      return true;
    },
    [avecJeton, dire, relire],
  );

  const enregistrerEncaissement = useCallback(async () => {
    if (brouillon === null) return;
    const champs = {
      recu_le: brouillon.recuLe,
      mode: brouillon.mode,
      reference: brouillon.reference.trim() === '' ? null : brouillon.reference.trim(),
      montant: saisieBrute(brouillon.montant),
      affectations: brouillon.lignes.map((l) => ({
        facture_id: l.facture_id,
        montant: l.montant,
      })),
    };
    const fait = await agir(async (j) =>
      brouillon.id === null
        ? saisirUnEncaissement(j, { ...champs, client_id: brouillon.clientId })
        : corrigerUnEncaissement(j, brouillon.id, champs),
    );
    if (fait) setBrouillon(null);
  }, [agir, brouillon]);

  const barre = (
    <div className="onglets-listes">
      {(
        [
          ['suivi', 'Suivi'],
          ['poste', 'Poste client'],
          ['relances', 'Relances'],
          ['encaissements', 'Encaissements'],
        ] as const
      ).map(([cle, nom]) => (
        <button
          key={cle}
          type="button"
          className={onglet === cle ? 'onglet-liste actif' : 'onglet-liste'}
          onClick={() => {
            setClientDuSuivi(null);
            setOnglet(cle);
          }}
        >
          {nom}
        </button>
      ))}
    </div>
  );

  return (
    <>
      {barre}
      {refus !== null && <p className="bandeau-refus">{refus}</p>}
      {panne && (
        <p className="bandeau-refus">
          Le recouvrement n’a pas pu être lu. Réessayez dans un instant.
        </p>
      )}

      {onglet === 'suivi' && (
        // LE SUIVI — lot 159, 03/11/2026 : lu chez Pennylane (planche
        // « Recouvrement · 6 »). L'ancienne vue reste jusqu'à l'effacement.
        <EcranSuiviPl
          jeton={jeton}
          quandAccesPerdu={quandAccesPerdu}
          version={version}
          quandAller={(vers, client) => {
            setClientDuSuivi(client ?? null);
            setOnglet(vers);
          }}
        />
      )}

      {/* LE POSTE CLIENT — lot 158, 03/11/2026 : lu chez Pennylane (planche
          « Recouvrement · 4 »). L'ancienne vue reste dans ce fichier jusqu'au
          lot de l'effacement. */}
      {onglet === 'poste' && (
        <EcranPostePl
          jeton={jeton}
          quandAccesPerdu={quandAccesPerdu}
          version={version}
          ouvertAuDepart={clientDuSuivi}
        />
      )}

      {/* LES RELANCES — lot 156, 03/11/2026 : refaites sur les factures
          Pennylane (cadrage de la refonte du Recouvrement). L'ancienne vue
          reste dans ce fichier jusqu'au lot de l'effacement. */}
      {onglet === 'relances' && (
        <EcranRelancesPl jeton={jeton} quandAccesPerdu={quandAccesPerdu} />
      )}

      {/* LES ENCAISSEMENTS — lot 158 : les factures payées chez Pennylane
          (planche « Recouvrement · 5 »). */}
      {onglet === 'encaissements' && (
        <EcranEncaissementsPl
          jeton={jeton}
          quandAccesPerdu={quandAccesPerdu}
          version={version}
          aujourdhui={aujourdhui}
        />
      )}

      {brouillon !== null && (
        <FenetreEncaissement
          brouillon={brouillon}
          clients={clients}
          pieces={pieces}
          occupe={occupe}
          quandChanger={setBrouillon}
          quandEnregistrer={() => {
            void enregistrerEncaissement();
          }}
          quandAnnuler={() => {
            setBrouillon(null);
          }}
        />
      )}

      {envoiDemande !== null && (
        <div className="voile-facture" role="presentation">
          <div
            className="fenetre-facture"
            role="dialog"
            aria-modal="true"
            aria-label="Marquer une relance envoyée"
          >
            <div className="entete-facture">
              <h2>
                {envoiDemande.niveauNom} — {envoiDemande.clientNom}
                <span className="secondaire">
                  {envoiDemande.pieces.map((piece) => piece.numero ?? '—').join(', ')}
                </span>
              </h2>
            </div>
            <div className="grille-saisie">
              <label>
                Envoyée le
                <input
                  className="champ"
                  type="date"
                  value={envoiLe}
                  disabled={occupe}
                  onChange={(e) => {
                    setEnvoiLe(e.target.value);
                  }}
                />
              </label>
              <label>
                Canal
                <select
                  className="champ"
                  value={envoiCanal}
                  disabled={occupe}
                  onChange={(e) => {
                    setEnvoiCanal(e.target.value);
                  }}
                >
                  {CANAUX_RELANCE.map((canal) => (
                    <option key={canal.cle} value={canal.cle}>
                      {canal.nom}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Montant réclamé
                <input
                  className="champ"
                  value={montant(envoiDemande.montant)}
                  disabled
                />
              </label>
            </div>
            {/* LA DATE EST CELLE DU COURRIER, pas celle de la saisie. C'est
                elle qui décide de la date du niveau suivant : la reculer d'une
                semaine avance toute la suite de la cadence d'autant. */}
            <p className="bandeau-attente">
              La date est celle où le courrier est <b>parti</b>, pas celle de la saisie.
              C’est elle qui commande la date du niveau suivant.
            </p>
            <div className="pied-facture">
              <button
                type="button"
                className="bouton"
                disabled={occupe || envoiLe === ''}
                onClick={() => {
                  void (async () => {
                    const fait = await agir(async (j) =>
                      marquerRelanceEnvoyee(j, {
                        client_id: envoiDemande.clientId,
                        niveau: envoiDemande.niveau,
                        canal: envoiCanal,
                        envoyee_le: envoiLe,
                        montant_reclame: (envoiDemande.montant / 100).toFixed(2),
                        facture_ids: envoiDemande.pieces.map((piece) => piece.id),
                      }),
                    );
                    if (fait) setEnvoiDemande(null);
                  })();
                }}
              >
                Marquer envoyée
              </button>
              <button
                type="button"
                className="bouton bouton-discret"
                disabled={occupe}
                onClick={() => {
                  setEnvoiDemande(null);
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {gelDemande !== null && (
        <div className="voile-facture" role="presentation">
          <div
            className="fenetre-facture"
            role="dialog"
            aria-modal="true"
            aria-label="Geler les relances"
          >
            <div className="entete-facture">
              <h2>
                Geler la relance — {gelDemande.numero ?? 'cette pièce'}
                <span className="secondaire">{gelDemande.client_nom}</span>
              </h2>
            </div>
            <div className="grille-saisie">
              <label>
                Motif
                <select
                  className="champ"
                  value={gelMotif}
                  disabled={occupe}
                  onChange={(e) => {
                    setGelMotif(e.target.value);
                  }}
                >
                  {MOTIFS_GEL.map((motif) => (
                    <option key={motif.cle} value={motif.cle}>
                      {motif.nom}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                La relance reprend le
                <input
                  className="champ"
                  type="date"
                  value={gelReprise}
                  disabled={occupe}
                  onChange={(e) => {
                    setGelReprise(e.target.value);
                  }}
                />
              </label>
            </div>
            {/* LA DATE DE REPRISE EST OBLIGATOIRE, et le bouton le montre plutôt
                que de laisser l'API refuser après coup. Sans elle, un gel n'est
                pas une décision : c'est un oubli. */}
            <p className="bandeau-attente">
              La date de reprise est obligatoire. Au jour dit, ce client revient dans
              les relances au niveau où il s’était arrêté, sans que personne n’y pense.
            </p>
            <div className="pied-facture">
              <button
                type="button"
                className="bouton"
                disabled={occupe || gelReprise === ''}
                onClick={() => {
                  void (async () => {
                    const fait = await agir(async (j) =>
                      gelerLesRelances(j, gelDemande.id, {
                        motif: gelMotif,
                        reprise_le: gelReprise,
                      }),
                    );
                    if (fait) setGelDemande(null);
                  })();
                }}
              >
                Geler
              </button>
              <button
                type="button"
                className="bouton bouton-discret"
                disabled={occupe}
                onClick={() => {
                  setGelDemande(null);
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** La phrase d'un refus. La carte est exhaustive : il y en a toujours une. */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a changé. Reconnectez-vous.',
  introuvable: 'Cette fiche est introuvable.',
  injoignable: 'Le service est injoignable. Réessayez dans un instant.',
  code_pris: 'Ce code est déjà pris.',
  invalide: 'Cette demande a été refusée : vérifiez ce qui a été saisi.',
  conflit: 'Cette période en recouvre une autre.',
  encore_actif: 'Cette fiche est encore active.',
  encore_rattache: 'Des lignes s’y rattachent encore.',
  ordre_annule: 'Cet ordre est annulé.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape.',
  etape_validee: 'Cette étape est validée : elle ne se déplace plus.',
  jour_sans_etape: 'Ce jour ne correspond à aucune étape de cet ordre.',
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  // LES DEUX REFUS DU BOUCLAGE D'UNE JOURNÉE, ajoutés le 14/09/2026. Cette
  // carte doit rester EXHAUSTIVE : c'est elle qui garantit qu'aucun refus de
  // l'API ne s'affichera un jour sous forme de code brut.
  journee_cloturee:
    'Cette journée est clôturée. Rouvrez-la avant de la corriger.',
  journee_sans_chainage:
    'La journée précédente de ce camion ne dit pas où il s’est arrêté : renseignez son lieu de fin d’abord.',
  litige_deja_ouvert: 'Un litige est déjà ouvert sur cette étape.',
  litige_deja_resolu: 'Ce litige est déjà résolu.',
  litige_ouvert: 'Un litige est ouvert sur cet ordre.',
  deja_facture: 'Cet ordre porte déjà une ligne de facture.',
  rien_a_facturer: 'Aucun de ces ordres n’attend de facture.',
  facture_emise: 'Cette pièce est émise : elle se corrige par un avoir.',
  facture_brouillon: 'Cette pièce est un brouillon : validez-la d’abord.',
  facture_vide: 'Une facture sans ligne ne se valide pas.',
  ordre_en_facturation: 'Cet ordre est parti en facturation.',
  affectation_excessive:
    'Vous affectez plus que ce qui a été reçu : corrigez la ventilation.',
  piece_non_emise:
    'Cette pièce est un brouillon : validez-la avant de la régler ou de la relancer.',
  piece_autre_client: 'Cette pièce appartient à un autre client.',
  gel_deja_actif:
    'Ce client a déjà un gel en cours : levez-le avant d’en poser un autre.',
  gel_absent: 'Ce client n’a aucun gel en cours.',
  // LES DEUX REFUS DE L'ANNULATION DE VALIDATION, ajoutés le 10/09/2026.
  numero_pas_le_dernier:
    'Cette pièce ne porte pas le dernier numéro de sa série : lui rendre son numéro laisserait un trou. Corrigez-la par un avoir.',
  facture_reglee:
    'Un règlement est affecté à cette pièce : détachez-le avant d’annuler la validation.',
  // AJOUTÉ LE 10/09/2026, sur un 500 constaté EN PRODUCTION : la base
  // refusait déjà (relance_pieces pointe sur factures en `on delete
  // restrict`), mais son refus sortait en erreur interne et l'écran
  // parlait d'une panne.
  piece_relancee:
    'Une relance a été envoyée sur cette pièce : elle ne peut plus être supprimée. Corrigez-la par un avoir.',
  facture_sans_echeance:
    'Cette pièce n’a pas de date d’échéance : sans elle, le recouvrement ne saura jamais qu’elle est en retard.',
  // AJOUTÉ LE 11/09/2026 avec le rattachement Pennylane. Distinct de
  // `code_pris` : le geste de réparation n'est pas le même. Le message NOMME
  // ce qu'il faut regarder — sans quoi il faudrait ouvrir les neuf autres
  // fiches pour trouver celle qui tient déjà ce client.
  pennylane_deja_rattache:
    'Ce client Pennylane est déjà rattaché à une autre fiche de l’annuaire. Le plus souvent, c’est que deux fiches Stafa désignent le même client : vérifiez vos doublons.',
  // AJOUTÉS LE 11/09/2026 avec l'envoi d'une facture chez Pennylane. Deux
  // messages et pas un : le premier mène à un AUTRE ÉCRAN, le second demande
  // de corriger la pièce. L'écran ne propose pas la même suite.
  pennylane_client_non_rattache:
    'Le client de cette facture n’est pas rattaché à un client Pennylane. Allez dans Paramètres → Pennylane pour le rattacher, puis réessayez.',
  pennylane_impossible:
    'Cette facture n’a pas pu être envoyée chez Pennylane. Le détail est affiché sous ce message : il vient de Pennylane et dit ce qui ne va pas.',
  facture_pas_deposee:
    'Cette facture n\u2019est pas encore d\u00e9pos\u00e9e chez Pennylane : envoyez-la d\u2019abord.',
  facture_modifiee_depuis_apercu:
    'Cette facture a changé depuis le dernier aperçu. Recliquez « Aperçu Pennylane », vérifiez le document, puis validez.',
  // LES QUATRE REFUS DE L'AVOIR, ajoutés le 11/09/2026. Quatre et pas un :
  // chacun porte une SUITE différente — ouvrir l'avoir existant, corriger un
  // montant, ou rien du tout quand le geste n'a pas de sens.
  avoir_sur_avoir:
    'On n’établit pas un avoir sur un avoir. Si cet avoir est faux, c’est une facture qu’il faut établir.',
  facture_deja_avoiree:
    'Cette facture est déjà intégralement avoirée : un second avoir rendrait au client plus qu’il n’a payé. Ouvrez l’avoir existant.',
  avoir_trop_grand:
    'Cet avoir dépasse ce qui reste à avoirer sur cette facture. Le détail affiché sous ce message donne le montant maximal.',
  avoir_impossible:
    'Cet avoir ne peut pas être établi. Le détail affiché sous ce message dit ce qui s’y oppose.',
  // AJOUTÉ LE 10/09/2026. Distinct de `injoignable`, qui parlait de connexion
  // alors que le service avait bel et bien répondu.
  service:
    'Le service a répondu une erreur inattendue. Ce n’est pas votre connexion. Ouvrez la console (F12) : le code y est écrit.',
};
